import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Typography } from "@mui/material";

import { ComplianceEnum } from "../../enum/jobEnums";
import { GinoFitScoreData } from "../../types/candidate";
import { GinoCandidateSummary, UnlockAdvancedHiringTools } from "../../components";
import { BackButtonIcon } from "../../assets/icons/candidate-details-page-icons";

import CandidateInfo from "./CandidateInfo";
import RequiredSubmission from "./RequiredSubmission";

const ginoFitScoreData: GinoFitScoreData = {
  candidateDetails: {
    // @ts-ignore
    user: {
      id: 19282,
      firstName: "Ahmed Youssef",
      lastName: "Player",
      email: "ahyousif@gmail.com",
      bio: "I am a football player",
      gender: "MALE",
      offersCount: {
        createdOffers: {
          profile: 0,
        },
        receivedOffers: {
          profile: 0,
        },
      },
      verificationStatus: "VERIFIED",
      status: null,
      city: "Alexandria",
      role: "PLAYER",
      subRole: null,
      birthdate: "February 09, 2015",
      country: {
        id: 62,
        name: "Egypt",
        url: "11fe9e629e901993952e7311b83caf59.png",
      },
      nationality: {
        id: 62,
        name: "Egyptian",
        url: "11fe9e629e901993952e7311b83caf59.png",
      },
      age: 10,
      countryId: 62,
      url: "404c6b03939f464d6502ff2843879ca4.jpeg",
      media: {
        url: "404c6b03939f464d6502ff2843879ca4.jpeg",
        type: "IMAGE",
        thumbnail: "",
      },
      // @ts-ignore
      createdAt: "2025-02-10T03:29:40.246Z",
      cityObject: {
        id: 153,
        longitude: "29.9167",
        latitude: "31.2",
        population: 4870000,
        adminName: "Al Iskandarīyah",
        capital: "admin",
        countryId: 62,
        localizedCity: {
          id: 305,
          cityId: 153,
          lang: "EN",
          name: "Alexandria",
        },
        name: "Alexandria",
      },
      height: 181,
      weight: 90,
      strongFoot: "RIGHT",
      otherPosition: "RIGHT WINGER (RW)",
      primaryPosition: "CENTER BACK (CB)",
      team: null,
      countriesWorkedIn: [],
      countriesWorkedInCount: 0,
      overview: [
        {
          key: "age",
          title: "Age",
          value: 10,
        },
        {
          key: "weight",
          title: "Weight",
          value: 90,
        },
        {
          key: "height",
          title: "Height",
          value: 181,
        },
        {
          key: "strongFoot",
          title: "Strong Foot",
          // @ts-ignore
          value: "RIGHT",
        },
      ],
      isFollowing: true,
    },
    fitScore: 80,
    fitScoreJustification:
      "He matches the required skills, including [Skill A, Skill B], and brings [X years] of relevant experience in a similar role. His education aligns well with the job requirements, and past performance indicators (where available) further support his suitability for this position.",
    // @ts-ignore
    candidateStatus: "SHORTLISTED",
    candidateOverview: [
      {
        key: "experience",
        value: "4 years",
      },
      {
        key: "languages",
        value: "English, Arabic",
      },
    ],
    languagesSpoken: ["English", "Arabic"],
    willingnessToRelocate: true,
    introductionVideo: {
      id: 13673,
      url: "fa3aafcf0c462baadc82770823a242cb.mp4",
      type: "VIDEO",
      thumbnail: "6ac614f609b8d4ce80121d241a6820a8.jpeg",
    },
    clubsAcademiesWorkedIn: ["Club A", "Club B"],
    certificateAndLicenses: [
      {
        title: "Certificate A",
        startDate: "2021-01-01",
        endDate: "2021-01-01",
      },
      {
        title: "Certificate B",
        startDate: "2021-01-01",
        endDate: "2021-01-01",
      },
      {
        title: "Certificate C",
        startDate: "2021-01-01",
        endDate: "2021-01-01",
      },
    ],
  },
  jobName: "Professional Football Player",
  summary:
    "Marcus Silva demonstrates exceptional technical ability and game intelligence, establishing himself as a dynamic central midfielder with strong playmaking capabilities. His vision and passing range make him a key creative force, while his work rate and tactical awareness contribute significantly to both offensive and defensive phases of play.",
  strengths: ["Strong certifications", "Good relocation flexibility", "Relevant clubs"],
  gaps: [
    "Only partial country match",
    "Aerial duels and heading ability",
    "missing Spanish language",
    "Crossing technique from wide positions",
  ],
  recommendation: "Candidate is a strong fit overall. Recommended for shortlist",
  requiredSubmission: [
    {
      title: "Languages Spoken",
      jobRequirement: ["English", "French", "Spanish", "Arabic"],
      candidateSubmitted: ["English", "Spanish"],
      compliance: ComplianceEnum.partiallyComply,
      fitScore: 40,
    },
    {
      title: "Willingness to Relocate",
      jobRequirement: true,
      candidateSubmitted: true,
      compliance: ComplianceEnum.comply,
      fitScore: 100,
    },
    {
      title: "Major Injury",
      jobRequirement: false,
      candidateSubmitted: true,
      compliance: ComplianceEnum.comply,
      fitScore: 0,
    },
    {
      title: "Introductory Video",
      jobRequirement: true,
      candidateSubmitted: true,
      compliance: ComplianceEnum.comply,
      fitScore: 100,
    },
    {
      title: "Specify sports entity",
      jobRequirement: ["La Liga", "Premier League"],
      candidateSubmitted: ["Sevilla FC", "Valencia"],
      compliance: ComplianceEnum.notComply,
      fitScore: 10,
    },
  ],
};

const CandidateGinoFitScorePage = () => {
  const { t, i18n } = useTranslation("goHire", { keyPrefix: "candidateGinoFitScorePage" });
  const navigate = useNavigate();

  const handleBack = () => navigate(-1);

  return (
    <Box sx={styles.container} component="main">
      <Box sx={styles.header}>
        <IconButton size="small" onClick={handleBack}>
          <BackButtonIcon />
        </IconButton>
        <Typography sx={styles.title}>{t("fittingScoreDetails")}</Typography>
      </Box>
      <UnlockAdvancedHiringTools />
      <CandidateInfo ginoFitScoreData={ginoFitScoreData} />
      <GinoCandidateSummary ginoFitScoreData={ginoFitScoreData} />
      <RequiredSubmission ginoFitScoreData={ginoFitScoreData} />
    </Box>
  );
};

export default CandidateGinoFitScorePage;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "30px",
  },
};
